import { terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_VIEWER_PERMISSION,
} from './cockpit-permissions';
import {
  GetMemoryOverviewQuerySchema,
  GetMemoryReadCountsBodySchema,
  ListMemoriesQuerySchema,
  ListMemoriesResponseSchema,
  MemoryObjectIdSchema,
  MemoryOverviewSchema,
  MemoryReadCountsSchema,
} from './memory-overview.schemas';
import {
  GetMemoryRecordQuerySchema,
  ListMemoryReadsQuerySchema,
  ListMemoryReadsResponseSchema,
  MemoryEvidenceBodySchema,
  MemoryRecordSchema,
} from './memory-record.schemas';

const MemoryTenantParamsSchema = z.object({
  tenantId: z.string().min(1),
  memoryId: MemoryObjectIdSchema,
});

const MemoryEvidenceParamsSchema = z.object({
  tenantId: z.string().min(1),
  evidenceId: MemoryObjectIdSchema,
});

export const cockpitMemoryRoutes = {
  getMemoryOverview: terraContract
    .meta({
      permissions: [COCKPIT_VIEWER_PERMISSION, COCKPIT_ADMIN_PERMISSION],
    })
    .route({
      method: 'GET',
      path: '/memory/overview',
      summary: 'Get owned memory store counts and read statistics',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: GetMemoryOverviewQuerySchema }))
    .output(MemoryOverviewSchema),
  listMemories: terraContract
    .meta({
      permissions: [COCKPIT_VIEWER_PERMISSION, COCKPIT_ADMIN_PERMISSION],
    })
    .route({
      method: 'GET',
      path: '/memory/items',
      summary: 'List owned memories across tenants',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListMemoriesQuerySchema }))
    .output(ListMemoriesResponseSchema),
  getMemoryReadCounts: terraContract
    .meta({
      permissions: [COCKPIT_VIEWER_PERMISSION, COCKPIT_ADMIN_PERMISSION],
    })
    .route({
      method: 'POST',
      path: '/memory/read-counts',
      summary: 'Count reads for up to 50 owned memories',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: GetMemoryReadCountsBodySchema }))
    .output(MemoryReadCountsSchema),
  getMemoryRecord: terraContract
    .meta({
      permissions: [COCKPIT_VIEWER_PERMISSION, COCKPIT_ADMIN_PERMISSION],
    })
    .route({
      method: 'GET',
      path: '/memory/tenants/{tenantId}/items/{memoryId}',
      summary:
        'Get one owned memory with history, lineage, evidence, and usage',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: MemoryTenantParamsSchema,
        query: GetMemoryRecordQuerySchema,
      }),
    )
    .output(MemoryRecordSchema),
  listMemoryReads: terraContract
    .meta({
      permissions: [COCKPIT_VIEWER_PERMISSION, COCKPIT_ADMIN_PERMISSION],
    })
    .route({
      method: 'GET',
      path: '/memory/tenants/{tenantId}/items/{memoryId}/reads',
      summary: 'List the reads of one owned memory, newest first',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: MemoryTenantParamsSchema,
        query: ListMemoryReadsQuerySchema,
      }),
    )
    .output(ListMemoryReadsResponseSchema),
  getMemoryEvidenceBody: terraContract
    .meta({
      permissions: [COCKPIT_VIEWER_PERMISSION, COCKPIT_ADMIN_PERMISSION],
    })
    .route({
      method: 'POST',
      path: '/memory/tenants/{tenantId}/evidence/{evidenceId}/body',
      summary: 'Read one evidence body of an owned memory',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: MemoryEvidenceParamsSchema }))
    .output(MemoryEvidenceBodySchema),
};
